<template>
  <div class="news-detail">
     <div class="nav-bg"></div>
    <div class="container">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <el-skeleton :rows="10" animated />
      </div>

      <!-- 错误提示 -->
      <div v-else-if="error" class="error-container">
        <el-empty :description="t('news.detail.fetchFailed')" />
        <el-button type="primary" @click="fetchNewsDetail">{{ t('news.detail.reload') }}</el-button>
      </div>

      <!-- 新闻内容 -->
      <div v-else-if="news" class="news-content">
        <!-- 标题 -->
        <h1 class="news-title">{{ news.title }}</h1>
        
        <!-- 发布时间 -->
        <div class="news-meta">
          <span class="publish-time">{{ formatDate(news.publishTime) }}</span>
        </div>
        
        <!-- 灰色横线 -->
        <div class="divider"></div>
        
        <!-- 内容 -->
        <div class="news-body" v-html="news.content"></div>
        
        <!-- 灰色横线 -->
        <div class="divider"></div>
        
        <!-- 上下篇按钮 -->
        <div class="news-nav">
          <el-button 
            v-if="prevNews" 
            type="default" 
            @click="goToNewsDetail(prevNews.id, prevNews.linkUrl)"
            class="prev-news"
          >
            {{ t('news.detail.prevNews') }}：{{ prevNews.title }}
          </el-button>
          <el-button 
            v-if="nextNews" 
            type="default" 
            @click="goToNewsDetail(nextNews.id, nextNews.linkUrl)"
            class="next-news"
          >
            {{ t('news.detail.nextNews') }}：{{ nextNews.title }}
          </el-button>
        </div>
        

      </div>
    </div>
            <!-- 更多新闻 -->
        <div class="more-news-section">
          <h2 class="more-news-title">{{ $t('news.moreNews') }}</h2>
          <div class="more-news-list">
            <div
              v-for="item in moreNewsList"
              :key="item.id"
              class="more-news-item"
              @click="goToNewsDetail(item.id, item.linkUrl)"
            >
              <div class="more-news-item-left">
                <img
                  v-if="item.coverImage"
                  :src="item.coverImage"
                  :alt="item.title"
                  class="more-news-cover-image"
                />
                <div v-else class="more-news-cover-placeholder"></div>
              </div>
              <div class="more-news-item-right">
                <h3 class="more-news-item-title">{{ item.title }}</h3>
                <p class="more-news-item-summary">{{ item.summary }}</p>
                <div class="more-news-item-meta">
                  <span class="more-news-item-time">{{ formatDate(item.publishTime) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ElSkeleton, ElEmpty, ElButton } from 'element-plus';
import api from '../api';

// 国际化
const { t } = useI18n();

// 获取路由参数
const route = useRoute();
const router = useRouter();
const newsId = computed(() => route.params.id);

// 响应式数据
const loading = ref(true);
const error = ref(false);
const news = ref(null);
const prevNews = ref(null);
const nextNews = ref(null);
const moreNewsList = ref([]);
const moreNewsLoading = ref(false);

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  // 根据当前语言环境格式化日期
  const language = navigator.language || 'zh-CN';
  return date.toLocaleDateString(language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// 获取新闻详情
const fetchNewsDetail = async () => {
  loading.value = true;
  error.value = false;
  
  try {
    const response = await api.news.getNews(newsId.value);
    if (response.success) {
      news.value = response.data;
      // 获取上下篇新闻
      await fetchPrevNextNews();
      // 获取更多新闻
      await fetchMoreNews();
    } else {
      error.value = true;
    }
  } catch (err) {
    console.error('获取新闻详情失败:', err);
    error.value = true;
  } finally {
    loading.value = false;
  }
};

// 获取上下篇新闻
const fetchPrevNextNews = async () => {
  try {
    // 这里需要根据实际的API接口来获取上下篇新闻
    // 假设API支持获取上下篇新闻的功能
    const response = await api.news.getNewsList({ page: 1, limit: 100 });
    if (response.success && response.data && response.data.list) {
      const newsList = response.data.list;
      const currentIndex = newsList.findIndex(item => item.id === parseInt(newsId.value));
      
      if (currentIndex > 0) {
        prevNews.value = newsList[currentIndex - 1];
      }
      
      if (currentIndex < newsList.length - 1) {
        nextNews.value = newsList[currentIndex + 1];
      }
    }
  } catch (err) {
    console.error('获取上下篇新闻失败:', err);
  }
};

// 获取更多新闻
const fetchMoreNews = async () => {
  moreNewsLoading.value = true;
  try {
    const response = await api.news.getNewsList({ page: 1, limit: 6 });
    if (response.success && response.data && response.data.list) {
      // 过滤掉当前新闻
      moreNewsList.value = response.data.list.filter(item => item.id !== parseInt(newsId.value));
    }
  } catch (err) {
    console.error('获取更多新闻失败:', err);
  } finally {
    moreNewsLoading.value = false;
  }
};

// 跳转到新闻详情或外部链接
const goToNewsDetail = (newsId, linkUrl) => {
  // 如果存在linkUrl，则直接跳转到外部链接
  router.push(`/news/${newsId}`);
  // 滚动到最上方
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// 组件挂载时获取新闻详情
onMounted(() => {
  fetchNewsDetail();
});
</script>

<style scoped>
.news-detail {
  background: white;
}
.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
}

.loading-container,
.error-container {
  padding: 40px 0;
}

.error-container {
  text-align: center;
}

.news-content {
  padding: 20px 0;
}

.news-title {
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #333;
  line-height: 1.3;
}

.news-meta {
  margin-bottom: 20px;
  color: #999;
  font-size: 14px;
}

.news-cover {
  margin-bottom: 30px;
}

.cover-image {
  width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.nav-bg {
  height: 70px;
  width: 100%;
  background: #222;
}

.news-summary {
  font-size: 16px;
  line-height: 1.6;
  color: #666;
  margin-bottom: 30px;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.news-body {
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 30px;
}

.news-body p {
  margin-bottom: 16px;
}

.news-body img {
  max-width: 100%;
  height: auto;
  margin: 20px 0;
  border-radius: 4px;
}

.news-body h2 {
  font-size: 22px;
  font-weight: bold;
  margin: 30px 0 15px;
  color: #333;
}

.news-body h3 {
  font-size: 20px;
  font-weight: bold;
  margin: 25px 0 12px;
  color: #333;
}

.news-body ul,
.news-body ol {
  margin: 16px 0;
  padding-left: 24px;
}

.news-body li {
  margin-bottom: 8px;
}

/* 灰色横线 */
.divider {
  width: 100%;
  height: 1px;
  background-color: #e0e0e0;
  margin: 20px 0;
}

/* 上下篇按钮 */
.news-nav {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
  flex-wrap: wrap;
  gap: 10px;
}

.prev-news,
.next-news {
  flex: 1;
  min-width: 200px;
  text-align: center;
}

.news-link {
  margin-top: 40px;
  text-align: center;
}

@media (max-width: 768px) {
  .news-title {
    font-size: 24px;
  }
  
  .container {
    padding: 0 15px;
  }
  
  .news-body {
    font-size: 15px;
  }
  
  .news-nav {
    flex-direction: column;
  }
  
  .prev-news,
  .next-news {
    width: 100%;
  }
}

/* 更多新闻样式 */
.more-news-section {
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid #e0e0e0;
}

.more-news-title {
  font-size: 3rem;
  transition: color 0.3s ease;
  text-align: center;
  margin-bottom: 40px;
  @media (max-width: 480px) {
    font-size: 2rem;
  }
}

.more-news-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
  margin: 40px;
}

.more-news-item {
  display: flex;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  height: 280px;
  padding: 12px;
}

.more-news-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
}

.more-news-item-left {
  flex: 0 0 300px;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
}

.more-news-cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.5s ease;
}

.more-news-item:hover .more-news-cover-image {
  transform: scale(1.05);
}

.more-news-cover-placeholder {
  width: 100%;
  height: 100%;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
  background-image: linear-gradient(45deg, #f5f5f5 25%, transparent 25%),
    linear-gradient(-45deg, #f5f5f5 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #f5f5f5 75%),
    linear-gradient(-45deg, transparent 75%, #f5f5f5 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
}

.more-news-item-right {
  flex: 1;
  padding: 30px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
}

.more-news-item-title {
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 20px;
  line-height: 1.4;
  transition: color 0.3s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.more-news-item:hover .more-news-item-title {
  color: #1890ff;
}

.more-news-item-summary {
  font-size: 16px;
  color: #666;
  line-height: 1.8;
  margin-bottom: 25px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  /* 添加底部内边距，隐藏可能露出的文字 */
  padding-bottom: 2px;
  /* 确保容器高度能完全容纳3行文字 */
  max-height: calc(16px * 1.8 * 3 + 2px);
}

.more-news-item-meta {
  display: flex;
  align-items: center;
  gap: 20px;
}

.more-news-item-time {
  font-size: 14px;
  color: #999;
  display: flex;
  align-items: center;
  gap: 5px;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .more-news-list {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 992px) {
  .more-news-item {
    height: 180px;
  }

  .more-news-list {
    grid-template-columns: repeat(1, 1fr);
    gap: 30px;
  }

  .more-news-item-left {
    flex: 0 0 200px;
  }

  .more-news-item-right {
    padding: 20px;
  }

  .more-news-item-title {
    font-size: 20px;
    margin-bottom: 15px;
  }

  .more-news-item-summary {
    font-size: 14px;
    -webkit-line-clamp: 2;
    max-height: calc(14px * 1.8 * 2 + 2px);
    padding-bottom: 2px;
  }
}

@media (max-width: 768px) {
  .more-news-list {
    grid-template-columns: 1fr;
    gap: 25px;
  }

  .more-news-item {
    flex-direction: column;
    height: auto;
  }

  .more-news-item-left {
    flex: none;
    height: 280px;
    width: 100%;
  }

  .more-news-item-right {
    padding: 20px;
  }

  .more-news-item-title {
    font-size: 20px;
    margin-bottom: 15px;
  }

  .more-news-item-summary {
    font-size: 14px;
    -webkit-line-clamp: 2;
    margin-bottom: 15px;
    max-height: calc(14px * 1.8 * 2 + 2px);
    padding-bottom: 2px;
  }

  .more-news-item-meta {
    gap: 15px;
  }
}

@media (max-width: 480px) {
  .more-news-item-left {
    height: 160px;
  }

  .more-news-item-right {
    padding: 15px;
  }

  .more-news-item-title {
    font-size: 18px;
  }

  .more-news-item-time {
    font-size: 13px;
  }
}
</style>